/* eslint-disable @next/next/no-img-element */
import { unit, C, Canvas, Abs, Avatar, BLogo, Icon, Sparkle } from './kit';

// Vector rebuild of /img/redesign/engage-visual-v4.png (1536×1024 canvas).
const W = 1536;
const H = 1024;
const u = unit(W);

const INK = '#15122A';
const MUTED = '#6E68A6';
const PURPLE = '#5B45F5';
const BTN = '#6A4FFA';
const CARD_BG = '#FCF9FC';
const CARD_LINE = '#EEE8F4';

// Text placed by the top of its capitals (as measured on the PNG).
// Regular-weight copy gets a touch of tracking: the render's face is wider than Inter Tight.
const T = ({ x, cap, f, wt = 500, color = INK, style, children }) => (
  <Abs u={u} x={x} y={cap - 0.26 * f} style={{
    fontSize: u(f), fontWeight: wt, color, letterSpacing: wt === 500 ? '0.025em' : undefined, ...style,
  }}>
    {children}
  </Abs>
);

const Emoji = ({ x, y, size, children }) => (
  <Abs u={u} x={x} y={y} w={size} h={size} style={{
    fontSize: u(size * 0.86), lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>{children}</Abs>
);

const Panel = ({ x, y, w, h, r = 16, bg = CARD_BG, style, children }) => (
  <Abs u={u} x={x} y={y} w={w} h={h} style={{
    background: bg, borderRadius: u(r), border: `${u(1)} solid ${CARD_LINE}`, boxSizing: 'border-box',
    boxShadow: `0 ${u(2)} ${u(8)} rgba(90, 60, 160, 0.05)`,
    ...style,
  }}>
    {children}
  </Abs>
);

const AppPill = ({ x, y, w = 40, h = 19, f = 12 }) => (
  <Abs u={u} x={x} y={y} w={w} h={h} style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#F3EDF7', borderRadius: u(h / 2), fontSize: u(f), fontWeight: 700, color: INK, letterSpacing: '0.02em',
  }}>APP</Abs>
);

const Info = ({ x, y, size = 16 }) => (
  <Abs u={u} x={x - 0.5} y={y - 0.5}><Icon u={u} name="info" size={size} color="#8A80D8" stroke={2.3} /></Abs>
);

const Star = ({ x, y, size }) => <Emoji x={x} y={y} size={size}>⭐</Emoji>;

const Mascot = ({ name, x, y, size, bg, scale = 1, dx = 0, dy = 0 }) => (
  <Abs u={u} x={x} y={y} w={size} h={size} style={{
    background: bg, borderRadius: u(size * 0.2), overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <img src={`/img/redesign/mascots/${name}.webp`} alt="" draggable="false" style={{
      width: u(size * scale), height: u(size * scale), display: 'block', flexShrink: 0,
      transform: `translate(${u(dx)}, ${u(dy)})`,
    }} />
  </Abs>
);

const Reaction = ({ x, y, w, emoji, count, at, h = 34 }) => (
  <Abs u={u} x={x} y={y} w={w} h={h} style={{
    border: `${u(1.5)} solid #ECEAF7`, borderRadius: u(h / 2), background: '#FDFCFE', boxSizing: 'border-box',
  }}>
    <Abs u={u} x={10} y={6} w={22} h={22} style={{
      fontSize: u(19), lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{emoji}</Abs>
    <Abs u={u} x={at} y={8} style={{ fontSize: u(15.5), fontWeight: 600, color: INK }}>{count}</Abs>
  </Abs>
);

const Btn = ({ x, y, w, h, f = 15.5, r = 8, children }) => (
  <Abs u={u} x={x} y={y} w={w} h={h} style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: `linear-gradient(180deg, #7257FB 0%, ${BTN} 100%)`, borderRadius: u(r), color: '#fff',
    fontSize: u(f), fontWeight: 600, boxShadow: `0 ${u(3)} ${u(8)} rgba(106, 79, 250, 0.25)`,
  }}>{children}</Abs>
);

// Small "tear-off" calendar icon used in the time slots.
const CalIcon = ({ x, y, size = 18 }) => (
  <Abs u={u} x={x} y={y} w={size} h={size}>
    <svg viewBox="0 0 20 20" style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden="true">
      <rect x="1.5" y="2.5" width="17" height="16" rx="2.5" fill="#fff" stroke="#8C8A96" strokeWidth="1.2" />
      <path d="M1.5 5a2.5 2.5 0 0 1 2.5-2.5h12a2.5 2.5 0 0 1 2.5 2.5v2.2h-17z" fill="#EF3B3B" />
      <path d="M6 1v3M14 1v3" stroke="#6B6976" strokeWidth="1.4" strokeLinecap="round" />
      {[0, 1, 2].map(r => [0, 1, 2, 3].map(c => (
        <rect key={`${r}${c}`} x={4.4 + c * 3.2} y={9.4 + r * 2.9} width="1.8" height="1.6" rx="0.3" fill="#8C8A96" />
      )))}
    </svg>
  </Abs>
);

// Purple 3D-ish calendar used for "Team Rituals".
const RitualIcon = ({ x, y, size = 26 }) => (
  <Abs u={u} x={x} y={y} w={size} h={size}>
    <svg viewBox="0 0 26 26" style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden="true">
      <defs>
        <linearGradient id="ev-rit" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7C63FF" />
          <stop offset="1" stopColor="#4F31F0" />
        </linearGradient>
      </defs>
      <rect x="1" y="3" width="24" height="22" rx="4" fill="url(#ev-rit)" />
      <rect x="1" y="3" width="24" height="5.5" rx="2.5" fill="#3D22D8" />
      <path d="M7.5 1v4.5M18.5 1v4.5" stroke="#3D22D8" strokeWidth="2.2" strokeLinecap="round" />
      {[0, 1, 2, 3, 4, 5].map(i => (
        <path key={i} d={`M${6.2 + i * 2.7} 11.5v9`} stroke="#fff" strokeWidth="1.3" strokeLinecap="round" opacity="0.92" />
      ))}
    </svg>
  </Abs>
);

// Purple megaphone used for shoutouts.
const Megaphone = ({ x, y, size = 26 }) => (
  <Abs u={u} x={x} y={y} w={size} h={size}>
    <svg viewBox="0 0 26 26" style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden="true">
      <defs>
        <linearGradient id="ev-meg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8B74FF" />
          <stop offset="1" stopColor="#4B2EF2" />
        </linearGradient>
      </defs>
      <path d="M8.5 16.5l2.4 6.8a1.6 1.6 0 0 1-3 1.1L5.2 17.6z" fill="#5A3CF4" />
      <rect x="1" y="9.2" width="6.5" height="8.6" rx="2" fill="#5A3CF4" />
      <path d="M6.8 9.4L21 2.2a1.2 1.2 0 0 1 1.7 1.1v20.4a1.2 1.2 0 0 1-1.7 1.1L6.8 17.6z" fill="url(#ev-meg)" />
      <ellipse cx="22.6" cy="13.5" rx="2.4" ry="10.8" fill="#6A50FA" />
      <ellipse cx="23" cy="13.5" rx="1.2" ry="7" fill="#A795FF" />
    </svg>
  </Abs>
);

const ValuePill = ({ x, y, w, bg, color, icon, iconX, textX, label }) => (
  <>
    <Abs u={u} x={x} y={y} w={w} h={35} style={{ background: bg, borderRadius: u(9) }} />
    <Abs u={u} x={iconX} y={y + 8} w={19} h={19}>{icon}</Abs>
    <T x={textX} cap={y + 13} f={14} wt={600} color={color}>{label}</T>
  </>
);

const svgFill = { width: '100%', height: '100%', display: 'block' };

const VALUE_ICONS = {
  star: (
    <svg viewBox="0 0 24 24" style={svgFill} aria-hidden="true">
      <path d="M12 1.8l3.1 6.3 6.9 1-5 4.9 1.2 6.9L12 17.6l-6.2 3.3L7 14 2 9.1l6.9-1z" fill="#1FC24A" stroke="#1FC24A" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  person: (
    <svg viewBox="0 0 24 24" style={svgFill} aria-hidden="true">
      <circle cx="12" cy="7.2" r="5" fill="#2F6BF2" />
      <path d="M2.5 22c0-5.2 4.2-8.5 9.5-8.5s9.5 3.3 9.5 8.5z" fill="#2F6BF2" />
    </svg>
  ),
  heart: (
    <svg viewBox="0 0 24 24" style={svgFill} aria-hidden="true">
      <path d="M12 21.5S1.5 15 1.5 8.2A5.4 5.4 0 0 1 12 5.6a5.4 5.4 0 0 1 10.5 2.6C22.5 15 12 21.5 12 21.5z" fill="#8A5CF6" />
    </svg>
  ),
  craft: (
    <svg viewBox="0 0 24 24" style={svgFill} aria-hidden="true">
      <defs>
        <linearGradient id="ev-craft" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFC23A" />
          <stop offset="1" stopColor="#F59A12" />
        </linearGradient>
      </defs>
      <path d="M9.2 2.5h5.6v4.1a2.3 2.3 0 0 1 0 4.6v-2h6.7v5.6h-4.1a2.3 2.3 0 0 1-4.6 0h2v6.7H9.2v-4.1a2.3 2.3 0 0 1 0-4.6v2H2.5V9.2h4.1a2.3 2.3 0 0 1 4.6 0h-2z" fill="url(#ev-craft)" transform="rotate(-12 12 12)" />
    </svg>
  ),
};

const Slot = ({ x, y, w, h, calX, textX, dotX, timeX, cap, f, date, time }) => (
  <>
    <Abs u={u} x={x} y={y} w={w} h={h} style={{
      background: '#FFFFFF', border: `${u(1)} solid #EEEAF6`, borderRadius: u(8), boxSizing: 'border-box',
    }} />
    <CalIcon x={calX} y={y + (h - 19) / 2} size={19} />
    <T x={textX} cap={cap} f={f} wt={600}>{date}</T>
    <Abs u={u} x={dotX} y={cap + 0.5 * f * 0.72 - 2} w={4} h={4} style={{ borderRadius: '50%', background: INK }} />
    <T x={timeX} cap={cap} f={f} wt={600}>{time}</T>
  </>
);

// [date, time, extra x offset for the bullet + time on the wider "Wed" row]
const SLOTS = [
  ['Tue, May 27', '10:00 AM – 10:30 AM', 0],
  ['Tue, May 27', '2:00 PM – 2:30 PM', 0],
  ['Wed, May 28', '11:00 AM – 11:30 AM', 1],
];

const SHOUTOUTS = [
  { avatar: 'neha', name: 'Neha', note: 'For going the extra mile!', pts: '+100', y: 720, dot: true },
  { avatar: 'karan', name: 'Karan', note: 'Always lifts the team up!', pts: '+75', y: 765 },
  { avatar: 'priya', name: 'Priya', note: 'Amazing client impact!', pts: '+50', y: 810, dot: true },
];

// Culture Pulse sparkline: smooth curve through the measured points.
const PULSE = [[1105, 987.5], [1165, 984], [1216, 991], [1306, 985], [1354, 974]];
const pulsePath = (() => {
  const pts = PULSE;
  let d = `M${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const mx = (x1 - x0) / 2;
    d += ` C${x0 + mx} ${y0} ${x1 - mx} ${y1} ${x1} ${y1}`;
  }
  return d;
})();

// Dashed connector arrows around the frame.
const ARROWS = [
  { d: 'M1420 251 C1478 262 1474 322 1426 349', head: [1421, 352, 145] },
  { d: 'M1428 538 C1466 505 1458 468 1436 453', head: [1432, 450, -130] },
  { d: 'M113 705 C96 668 106 640 148 632', head: [152, 631, -12] },
];

export default function EngageVisual({ className }) {
  return (
    <Canvas W={W} H={H} className={className} label="Engagement in your chat: Clappy kudos, Billy work anniversaries and Alfy coffee chats, next to a culture overview with engagement score, core values, rituals, shoutouts and culture pulse">
      {/* Decorations */}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }} aria-hidden="true">
        {ARROWS.map((a, i) => (
          <g key={i} stroke="#8F72F2" fill="none" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round">
            <path d={a.d} strokeDasharray="8 11" />
            <path d="M-10 -9 L0 0 L-10 9" transform={`translate(${a.head[0]} ${a.head[1]}) rotate(${a.head[2]})`} strokeWidth="5" />
          </g>
        ))}
      </svg>
      <Sparkle u={u} x={121.5} y={105.5} size={26} color="#F8D257" />
      <Sparkle u={u} x={133} y={153} size={26} color="#A08BF6" />
      <Sparkle u={u} x={1440.5} y={222} size={30} color="#F8D257" />
      <Sparkle u={u} x={1426.5} y={775.5} size={28} color="#F8D257" />
      <Sparkle u={u} x={1435} y={828} size={26} color="#A08BF6" />

      {/* Outer app frame */}
      <Abs u={u} x={191} y={13} w={1209} h={1002} style={{
        background: '#F8F2FC', borderRadius: u(34), border: `${u(2.5)} solid #FFFFFF`, boxSizing: 'border-box',
        boxShadow: `0 ${u(2)} ${u(6)} rgba(90, 60, 160, 0.06)`,
      }} />

      {/* Sidebar */}
      <Abs u={u} x={203} y={25} w={77} h={940} style={{ background: '#1C1631', borderRadius: u(22) }} />
      <Abs u={u} x={212} y={41}><BLogo u={u} size={56} /></Abs>
      {[['home', 155.5, 38], ['bot', 225.5, 38], ['bell', 295.5, 36], ['plus', 436.5, 40]].map(([name, cy, s]) => (
        <Abs key={name} u={u} x={240.5 - s / 2} y={cy - s / 2}>
          <Icon u={u} name={name} size={s} color="#fff" stroke={2.4} />
        </Abs>
      ))}
      <Abs u={u} x={213} y={342} w={55} h={55} style={{
        background: 'linear-gradient(160deg, #7556FA 0%, #6243F6 100%)', borderRadius: u(12),
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
      }}>
        <Icon u={u} name="hash" size={38} stroke={3} />
      </Abs>
      <Abs u={u} x={212} y={604}><Avatar u={u} name="me" size={57} dot="#1C1631" /></Abs>

      {/* Channel */}
      <Abs u={u} x={285} y={25} w={772} h={978} style={{
        background: CARD_BG, borderRadius: u(22), border: `${u(1)} solid ${CARD_LINE}`, boxSizing: 'border-box',
      }} />
      <T x={330} cap={55} f={29} wt={700}>#</T>
      <T x={366} cap={55} f={30} wt={700}>culture</T>

      {/* Clappy: kudos */}
      <Mascot name="clappy" x={328} y={117} size={72} bg="linear-gradient(160deg, #E0FFD8 0%, #D2F8CA 100%)" scale={0.87} dy={-1} />
      <T x={420} cap={125} f={18.5} wt={700}>Clappy</T>
      <AppPill x={495} y={123} />
      <T x={550} cap={128} f={15} color={MUTED}>10:42 AM</T>
      <T x={421} cap={160} f={18}>Great work team!</T>
      <Emoji x={571} y={152} size={28}>👏</Emoji>
      <T x={421} cap={194} f={17.5}>Priya just received a kudos for living our value</T>
      <Abs u={u} x={419} y={221} w={143} h={30} style={{
        background: '#E3F8E7', borderRadius: u(6), display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: u(17), fontWeight: 600, color: '#1E8F2C', letterSpacing: '0.02em',
      }}>Customer First</Abs>

      <Abs u={u} x={419} y={262} w={388} h={157} style={{
        border: `${u(1.2)} solid #FBE4CC`, borderRadius: u(12), boxSizing: 'border-box',
      }}>
        <Abs u={u} x={0} y={104} w={386} h={1.2} style={{ background: '#FBE7D3' }} />
      </Abs>
      <Abs u={u} x={433} y={276}><Avatar u={u} name="neha" size={41} dot="#fff" /></Abs>
      <T x={491} cap={283} f={15.5} wt={700}>Neha</T>
      <T x={723} cap={283} f={16} wt={700} color={PURPLE}>+100</T>
      <Star x={767} y={276.5} size={23} />
      <T x={491} cap={314} f={15.8}>Priya went above and beyond to help</T>
      <T x={491} cap={339} f={15.8}>our client meet a tight deadline!</T>
      <Reaction x={428} y={377} w={72} emoji="⭐" count="24" at={40} />
      <Reaction x={506} y={377} w={69} emoji="❤️" count="8" at={43} />
      <Reaction x={581} y={377} w={69} emoji="🎉" count="2" at={43} />

      {/* Billy: work anniversary */}
      <Mascot name="billy" x={327} y={438} size={70} bg="linear-gradient(160deg, #E2E5FF 0%, #D6DAFE 100%)" scale={0.85} />
      <T x={418} cap={451} f={18.5} wt={700}>Billy</T>
      <AppPill x={466} y={448} />
      <T x={521} cap={453} f={15} color={MUTED}>9:00 AM</T>
      <T x={419} cap={485} f={17.5}>It’s Karan’s work anniversary!</T>
      <Emoji x={662} y={477} size={28}>🎉</Emoji>
      <Abs u={u} x={418} y={513} w={389} h={105} style={{ background: '#EFE7FB', borderRadius: u(12) }} />
      <T x={438} cap={532} f={17.8}>Happy 2nd work anniversary, Karan!</T>
      <Emoji x={741} y={524} size={28}>🥳</Emoji>
      <T x={438} cap={562} f={16.2}>Thanks for being such an important</T>
      <T x={438} cap={589} f={16.2}>part of our journey.</T>
      <Reaction x={418} y={625} w={81} emoji="❤️" count="15" at={47} />
      <Reaction x={506} y={625} w={76} emoji="🎉" count="6" at={47} />

      {/* Alfy: coffee chat scheduling */}
      <Mascot name="alfy" x={415} y={687} size={64} bg="linear-gradient(160deg, #FFF4D9 0%, #FDEBC9 100%)" scale={0.78} dx={-1} />
      <T x={496} cap={698} f={18.5} wt={700}>Alfy</T>
      <AppPill x={543} y={696} />
      <T x={597} cap={700} f={15} color={MUTED}>11:30 AM</T>
      <T x={497} cap={731} f={17}>Let’s catch up! Here are some times that work for you:</T>
      <Abs u={u} x={493} y={761} w={382} h={189} style={{
        border: `${u(1.2)} solid #EEEAF6`, borderRadius: u(12), boxSizing: 'border-box',
      }} />
      {SLOTS.map(([date, time, dx], i) => (
        <Slot key={i} x={507} y={765 + i * 41.5} w={355} h={35} calX={517} textX={551} dotX={653 + dx} timeX={671 + dx}
          cap={777 + i * 41.5} f={16.5} date={date} time={time} />
      ))}
      <Btn x={507} y={901} w={98} h={34}>Book time</Btn>

      {/* Right column: culture overview */}
      <Panel x={1073} y={25} w={315} h={191} />
      <T x={1095} cap={46} f={16.9} wt={700}>Culture Overview</T>
      <Info x={1244} y={44} />
      <Abs u={u} x={1121} y={75} w={126} h={126}>
        <svg viewBox="0 0 126 126" style={svgFill} aria-hidden="true">
          <defs>
            <linearGradient id="ev-ring" x1="0.8" y1="0" x2="0.2" y2="1">
              <stop offset="0" stopColor="#5ED94A" />
              <stop offset="0.5" stopColor="#22D05A" />
              <stop offset="1" stopColor="#2FD040" />
            </linearGradient>
          </defs>
          <circle cx="63" cy="63" r="57.5" fill="none" stroke="#E2F6E6" strokeWidth="11" />
          <circle cx="63" cy="63" r="57.5" fill="none" stroke="url(#ev-ring)" strokeWidth="11" strokeLinecap="round"
            pathLength="100" strokeDasharray="82.5 100" transform="rotate(-86 63 63)" />
        </svg>
      </Abs>
      <Abs u={u} x={1124} y={105} w={120} style={{ textAlign: 'center', fontSize: u(27), fontWeight: 700, lineHeight: 1 }}>87%</Abs>
      <Abs u={u} x={1124} y={141} w={120} style={{ textAlign: 'center', fontSize: u(12.5), fontWeight: 600, lineHeight: 1.3 }}>
        Engagement<br />Score
      </Abs>
      <Abs u={u} x={1267} y={123}><Icon u={u} name="arrowUp" size={17} color="#1DBF3A" stroke={2.8} /></Abs>
      <T x={1289} cap={125} f={16.5} wt={600} color="#1DBF3A">12%</T>
      <T x={1329} cap={125} f={16} wt={600} color={MUTED}>from</T>
      <T x={1269} cap={148} f={16.5} wt={600} color={MUTED}>last month</T>

      {/* Core values */}
      <Panel x={1073} y={222} w={315} h={142} />
      <T x={1095} cap={242} f={16.7} wt={700}>Our Core Values</T>
      <Info x={1231} y={240} />
      <ValuePill x={1090} y={268} w={152} bg="#E2F8E8" color="#1E8F2C" icon={VALUE_ICONS.star} iconX={1104} textX={1133} label="Customer First" />
      <ValuePill x={1251} y={268} w={121} bg="#E4EBFD" color="#2557EE" icon={VALUE_ICONS.person} iconX={1265} textX={1293} label="Ownership" />
      <ValuePill x={1091} y={314} w={115} bg="#F1ECFC" color="#5A3CEB" icon={VALUE_ICONS.heart} iconX={1105} textX={1135} label="Kindness" />
      <ValuePill x={1218} y={314} w={100} bg="#FCF0E6" color="#F0561E" icon={VALUE_ICONS.craft} iconX={1234} textX={1264} label="Craft" />

      {/* Team rituals */}
      <Panel x={1073} y={371} w={315} h={147} />
      <RitualIcon x={1092} y={384} />
      <T x={1137} cap={392} f={16} wt={700}>Team Rituals</T>
      <Emoji x={1093} y={418} size={26}>💻</Emoji>
      <T x={1138} cap={426} f={14.5} wt={600}>Friday demo</T>
      <Emoji x={1092} y={449} size={26}>🎉</Emoji>
      <T x={1138} cap={456} f={14.5} wt={600}>Weekly wins</T>
      <Megaphone x={1093} y={480} />
      <T x={1137} cap={487} f={14.5} wt={600}>Team shoutout</T>

      {/* Coffee chats */}
      <Panel x={1073} y={525} w={315} h={141} />
      <Mascot name="alfy" x={1077} y={535} size={60} bg="linear-gradient(160deg, #FFF4D9 0%, #FDEBC9 100%)" scale={0.78} />
      <T x={1148} cap={547} f={16} wt={700}>Coffee Chats</T>
      <Info x={1254} y={545} />
      <T x={1148} cap={577} f={13.5} wt={600}>12 new matches this week</T>
      <Btn x={1093} y={613} w={119} h={34} f={14.5}>View matches</Btn>
      <Abs u={u} x={1288} y={600} w={56} h={56}>
        <svg viewBox="0 0 56 56" style={svgFill} aria-hidden="true">
          <circle cx="28" cy="28" r="26.5" fill="none" stroke="#F0443C" strokeWidth="2.6" strokeDasharray="7 6" strokeLinecap="round" />
        </svg>
      </Abs>
      <Abs u={u} x={1265.3} y={604.8}><Avatar u={u} name="engage-coffee-man" size={46} ring={2.5} /></Abs>
      <Abs u={u} x={1319.5} y={605.6}><Avatar u={u} name="engage-coffee-woman" size={46} ring={2.5} /></Abs>

      {/* Shoutouts */}
      <Panel x={1073} y={673} w={315} h={177} />
      <Megaphone x={1094} y={686} />
      <T x={1136} cap={693} f={15.5} wt={700}>Shoutouts</T>
      <Info x={1218} y={691} />
      {SHOUTOUTS.map(s => (
        <div key={s.name}>
          <Abs u={u} x={1093} y={s.y}><Avatar u={u} name={s.avatar} size={31} dot={s.dot ? '#fff' : undefined} /></Abs>
          <T x={1138} cap={s.y + 2.5} f={13.2} wt={700}>{s.name}</T>
          <T x={1138} cap={s.y + 20.5} f={11.7} wt={600}>{s.note}</T>
          <Abs u={u} x={1270} y={s.y + 6.5 - 0.26 * 16} w={71} style={{ textAlign: 'right', fontSize: u(16), fontWeight: 700, color: PURPLE }}>{s.pts}</Abs>
          <Star x={1346} y={s.y + 0.5} size={20} />
        </div>
      ))}

      {/* Culture pulse */}
      <Panel x={1073} y={857} w={315} h={147} />
      <Abs u={u} x={1094} y={870} w={25} h={25} style={{
        borderRadius: '50%', background: 'linear-gradient(160deg, #7E66FA 0%, #5638F2 100%)',
      }}>
        <svg viewBox="0 0 25 25" style={svgFill} aria-hidden="true">
          <circle cx="9" cy="10" r="1.6" fill="#fff" />
          <circle cx="16" cy="10" r="1.6" fill="#fff" />
          <path d="M7.8 14.2c2.4 2.8 7 2.8 9.4 0" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        </svg>
      </Abs>
      <T x={1133} cap={877} f={16} wt={700}>Culture Pulse</T>
      <Info x={1245} y={875} />
      <T x={1095} cap={904} f={15}>How’s our vibe this week?</T>
      <Abs u={u} x={1106} y={917} w={62} h={62} style={{ borderRadius: '50%', background: 'rgba(120, 220, 120, 0.16)' }} />
      <Abs u={u} x={1111} y={922} w={52} h={52} style={{
        borderRadius: '50%',
        background: 'radial-gradient(circle at 38% 30%, #8EF07A 0%, #49D545 45%, #2DBB35 100%)',
        boxShadow: `0 ${u(3)} ${u(8)} rgba(40, 170, 60, 0.25)`,
      }}>
        <svg viewBox="0 0 52 52" style={svgFill} aria-hidden="true">
          <ellipse cx="18.5" cy="21" rx="2.8" ry="3.6" fill="#16301A" />
          <ellipse cx="33.5" cy="21" rx="2.8" ry="3.6" fill="#16301A" />
          <path d="M15 31c5.5 6 16.5 6 22 0" stroke="#16301A" strokeWidth="3" fill="none" strokeLinecap="round" />
        </svg>
      </Abs>
      <T x={1193} cap={928} f={24} wt={700}>4.6<span style={{ fontWeight: 600 }}>/5</span></T>
      <T x={1193} cap={956} f={15} wt={600}>Great vibes!</T>
      <Emoji x={1276} y={950} size={20}>🎉</Emoji>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} aria-hidden="true">
        <defs>
          <linearGradient id="ev-pulse" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3DD25A" stopOpacity="0.18" />
            <stop offset="1" stopColor="#3DD25A" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${pulsePath} L1354 1000 L1105 1000 Z`} fill="url(#ev-pulse)" />
        <path d={pulsePath} fill="none" stroke="#3DD25A" strokeWidth="2.2" strokeLinecap="round" />
        {PULSE.map(([x, y]) => <circle key={x} cx={x} cy={y} r="4" fill="#22C845" />)}
      </svg>

      {/* Floating Alfy scheduling card (slightly tilted) */}
      <Abs u={u} x={54} y={710} w={356} h={280} style={{
        transform: 'rotate(-2.1deg)', transformOrigin: '50% 50%',
        background: '#FBF9FC', borderRadius: u(18), border: `${u(1)} solid ${CARD_LINE}`, boxSizing: 'border-box',
        boxShadow: `0 ${u(14)} ${u(34)} rgba(60, 40, 120, 0.14), 0 ${u(2)} ${u(6)} rgba(60, 40, 120, 0.06)`,
      }}>
        <T x={21} cap={23} f={15.6} wt={700}>Alfy Scheduling Coordinator</T>
        <AppPill x={236} y={20} w={33} h={18} f={10.5} />
        <T x={21} cap={55} f={14.1}>Here are some times that work for both of you:</T>
        {SLOTS.map(([date, time, dx], i) => (
          <Slot key={i} x={11} y={88 + i * 43.5} w={333} h={39} calX={28} textX={58} dotX={152 + dx * 4} timeX={169 + dx * 4}
            cap={102 + i * 43} f={15} date={date} time={time} />
        ))}
        <Btn x={17} y={227} w={94} h={34} f={15}>Book time</Btn>
      </Abs>
    </Canvas>
  );
}
